import { LayerModelLayerType } from 'api/models';

/**
 * Semantic color map per LayerModelLayerType.
 *
 * Visual/material layers use approximate real-world material colors.
 * Functional/snap layers use bright, visually distinct hues so content devs
 * can quickly distinguish placement areas, ranges, and snap points.
 *
 * Colors are derived from the ACWT layer system (layer.json in the GLB generator).
 */
export const LAYER_COLORS: Partial<Record<LayerModelLayerType, string>> = {
    // ── Default / Symbol ─────────────────────────────────────────────────
    [LayerModelLayerType.SYMBOL]: '#00ff00',
    [LayerModelLayerType.DECOR]: '#ff80ff',
    [LayerModelLayerType.COVERAREA]: '#87cefa',

    // ── Sheet / Panel materials ───────────────────────────────────────────
    [LayerModelLayerType.SHEDOKA]: '#c4a87e',
    [LayerModelLayerType.SHEBRO]: '#8b6914',
    [LayerModelLayerType.SHEGRE]: '#4a7c59',
    [LayerModelLayerType.SHEYEL]: '#e8c840',

    // ── Lacquer ───────────────────────────────────────────────────────────
    [LayerModelLayerType.LACBLU]: '#3a6fb5',
    [LayerModelLayerType.LACGRE]: '#4aac5e',
    [LayerModelLayerType.LACRED]: '#c0392b',
    [LayerModelLayerType.LACYEL]: '#f0c040',
    [LayerModelLayerType.DLACBLU]: '#2a5090',
    [LayerModelLayerType.DLACYEL]: '#d0a020',

    // ── Plastic / Plywood ─────────────────────────────────────────────────
    [LayerModelLayerType.PLABLU]: '#5587c0',
    [LayerModelLayerType.PLYBRO]: '#7a5030',

    // ── Timber ───────────────────────────────────────────────────────────
    [LayerModelLayerType.TIMBAU]: '#c8a060',
    [LayerModelLayerType.TIMBRO]: '#7a4020',
    [LayerModelLayerType.TIMYEL]: '#d4a830',
    [LayerModelLayerType.DTIMBAU]: '#b89050',
    [LayerModelLayerType.DTIMYEL]: '#b48020',

    // ── Zinc ─────────────────────────────────────────────────────────────
    [LayerModelLayerType.ZINK]: '#a0a8a0',
    [LayerModelLayerType.ZINKFX]: '#8898a0',
    [LayerModelLayerType.DZINK]: '#909890',
    [LayerModelLayerType.DZINKFX]: '#7888a0',

    // ── Aluminium ─────────────────────────────────────────────────────────
    [LayerModelLayerType.ALU]: '#c0c8d0',
    [LayerModelLayerType.DALU]: '#b0b8c8',
    [LayerModelLayerType.DSHEALU]: '#a8c0c8',

    // ── Lining / Rubber ───────────────────────────────────────────────────
    [LayerModelLayerType.LINBLA]: '#303030',
    [LayerModelLayerType.DLINBLA]: '#202020',
    [LayerModelLayerType.KUNBLA]: '#404040',
    [LayerModelLayerType.KUNGRY]: '#707070',
    [LayerModelLayerType.DKUNGRY]: '#606060',

    // ── Steel ─────────────────────────────────────────────────────────────
    [LayerModelLayerType.STENON]: '#d0c0b0',

    // ── Functional / Snap positions (bright distinct hues) ────────────────
    [LayerModelLayerType.BRACKETPOSITION]: '#ff6b35',
    [LayerModelLayerType.CLAMPPOSITION]: '#ff1493',
    [LayerModelLayerType.CLAMPRANGE]: '#ff69b4',
    [LayerModelLayerType.ADJUSTABLECLAMPPOSITION]: '#9400d3',
    [LayerModelLayerType.CROSSPOSITION]: '#00ff80',
    [LayerModelLayerType.TIERODPOSITION]: '#00d4ff',
    [LayerModelLayerType.TIERODPOSITION1]: '#00b8dc',
    [LayerModelLayerType.TIERODPOSITION2]: '#009cbf',
    [LayerModelLayerType.PINPOSITION]: '#ffd700',
    [LayerModelLayerType.BOLTPOSITION]: '#ff8c00',
    [LayerModelLayerType.UNICLAMPPOSITION]: '#00b8dc',
    [LayerModelLayerType.UNIVERSALPOSITION]: '#bf00ff',
    [LayerModelLayerType.PLUMBINGSTRUTPOSITIONL]: '#40e0d0',
    [LayerModelLayerType.PLUMBINGSTRUTPOSITIONR]: '#00ced1',
    [LayerModelLayerType.PLUMBINGSTRUTRANGE]: '#20b2aa',
    [LayerModelLayerType.TOPPLATFORMRANGE]: '#9370db',
    [LayerModelLayerType.WALLINGRANGE]: '#3cb371',
    [LayerModelLayerType.TABLEBEAM]: '#ff4500',

    // ── Structural frame / special ────────────────────────────────────────
    [LayerModelLayerType.SPRO]: '#c8c8ff',
    [LayerModelLayerType.SFAL]: '#ffd0d0',
    [LayerModelLayerType.STRI]: '#d0ffd0',
    [LayerModelLayerType.SFRAME]: '#d0d0ff',
    [LayerModelLayerType.SELEM]: '#ffeedd',
    [LayerModelLayerType.SHEAD]: '#ffe0b0',
    [LayerModelLayerType.SHEADBLU]: '#b0d0ff',
    [LayerModelLayerType.SHEADBLUL]: '#90c0ff',
    [LayerModelLayerType.SHEADGRY]: '#d0d0d0',
};

export const DEFAULT_LAYER_COLOR = '#808080';

export function getLayerColor(layerType: LayerModelLayerType): string {
    return LAYER_COLORS[layerType] ?? DEFAULT_LAYER_COLOR;
}
